import { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import MaterielAddModal from '../components/materiels/MaterielAddModal';
import MaterielAssignModal from '../components/materiels/MaterielAssignModal';
import MaterielDrawer from '../components/materiels/MaterielDrawer';
import MaterielEmptyState from '../components/materiels/MaterielEmptyState';
import MaterielFiltersBar from '../components/materiels/MaterielFiltersBar';
import MaterielKpiRow from '../components/materiels/MaterielKpiRow';
import MaterielListRow from '../components/materiels/MaterielListRow';
import MaterielStockConfirmModal from '../components/materiels/MaterielStockConfirmModal';
import { normalizeCommercantsList } from '../materiels/materielCommercePicker';
import { friendlyMaterielActionError } from '../materiels/materielActionErrors';
import {
  API_LIST_SOFT_LIMIT,
  computeParcKpis,
  filterAndSortParc,
  filtersFromChip,
  filtersFromKpi,
  resetParcFilters,
} from '../materiels/materielParcFilters';

function activeKpiFromFilters(filters) {
  if (filters.chip === 'a_traiter') return 'a_traiter';
  if (filters.chip === 'affectes' || filters.lifecycle === 'AFFECTE') {
    return 'affectes';
  }
  if (filters.chip === 'en_stock' || filters.lifecycle === 'EN_STOCK') {
    return 'en_stock';
  }
  if (
    (filters.chip === 'all' || !filters.chip) &&
    !filters.lifecycle &&
    !filters.type &&
    !filters.diagnostic &&
    !String(filters.query || '').trim()
  ) {
    return 'total';
  }
  return null;
}

export default function Materiels() {
  const { toasts, toast, removeToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => resetParcFilters());
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [journal, setJournal] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const [commerces, setCommerces] = useState([]);
  const [commercesLoading, setCommercesLoading] = useState(false);
  const [commercesError, setCommercesError] = useState(null);
  const [commercesLoaded, setCommercesLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listMateriels({});
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      toast.error(err?.message || 'Erreur chargement matériels');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setFilters((prev) =>
      prev.query === searchInput ? prev : { ...prev, query: searchInput }
    );
  }, [searchInput]);

  const loadCommerces = useCallback(async () => {
    setCommercesLoading(true);
    setCommercesError(null);
    try {
      const data = await apiService.getCommercants({
        pageSize: 500,
        includeInactive: 'true',
      });
      setCommerces(normalizeCommercantsList(data));
      setCommercesLoaded(true);
    } catch (err) {
      setCommercesError(
        friendlyMaterielActionError(
          err,
          'Impossible de charger la liste des points de vente.'
        )
      );
      setCommerces([]);
    } finally {
      setCommercesLoading(false);
    }
  }, []);

  const ensureCommerces = useCallback(() => {
    if (!commercesLoaded && !commercesLoading) {
      loadCommerces();
    }
  }, [commercesLoaded, commercesLoading, loadCommerces]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const d = await apiService.getMateriel(id);
      setDetail(d);
    } catch (err) {
      setDetail(null);
      setDetailError(err?.message || 'Erreur chargement fiche');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadJournal = useCallback(async (id) => {
    if (!id) return;
    setJournalLoading(true);
    setJournalError(null);
    try {
      const j = await apiService.getMaterielJournal(id, 30);
      setJournal(Array.isArray(j?.items) ? j.items : []);
    } catch (err) {
      setJournal([]);
      setJournalError(err?.message || 'Erreur chargement journal');
    } finally {
      setJournalLoading(false);
    }
  }, []);

  const openDetail = useCallback(
    (id) => {
      setSelectedId(id);
      setDetail(null);
      setDetailError(null);
      setJournal([]);
      setJournalError(null);
      loadDetail(id);
      loadJournal(id);
    },
    [loadDetail, loadJournal]
  );

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setJournal([]);
    setJournalError(null);
    setDetailLoading(false);
    setJournalLoading(false);
  };

  const refreshAfterMutation = async (id) => {
    await load();
    if (id) {
      setSelectedId(id);
      await Promise.all([loadDetail(id), loadJournal(id)]);
    }
  };

  const kpis = useMemo(() => computeParcKpis(items), [items]);
  const visible = useMemo(
    () => filterAndSortParc(items, filters),
    [items, filters]
  );
  const selectedItem = useMemo(
    () => items.find((d) => d.id === selectedId) || null,
    [items, selectedId]
  );
  const drawerItem = detail || selectedItem;
  const activeKpi = activeKpiFromFilters(filters);
  const hitApiLimit = items.length >= API_LIST_SOFT_LIMIT;

  const clearFilters = () => {
    setSearchInput('');
    setFilters(resetParcFilters(filters.sort));
  };

  const openAdd = () => {
    setAddOpen(true);
    ensureCommerces();
  };

  const handleAddSubmit = async ({
    serialNumber,
    type,
    model,
    label,
    assignNow,
    commerce,
  }) => {
    const payload = {
      serialNumber,
      type,
      model,
      label,
      provider: 'SUNMI',
      printEnabled: type === 'PRINTER_CLOUD',
      status: assignNow ? 'AFFECTE' : 'EN_STOCK',
    };
    if (assignNow && commerce?.id) {
      payload.commercantId = commerce.id;
    }
    const created = await apiService.createMateriel(payload);
    const newId = created?.id;
    toast.success(
      assignNow && commerce?.nom
        ? `Matériel ajouté et affecté à ${commerce.nom}.`
        : 'Matériel ajouté au stock.'
    );
    setAddOpen(false);
    await refreshAfterMutation(newId);
  };

  const handleAssignSubmit = async ({ commerce }) => {
    if (!selectedId || !commerce?.id) return;
    setActionBusy(true);
    try {
      await apiService.updateMateriel(selectedId, {
        commercantId: commerce.id,
        status: 'AFFECTE',
      });
      toast.success(`Matériel affecté à ${commerce.nom}.`);
      setAssignOpen(false);
      await refreshAfterMutation(selectedId);
    } finally {
      setActionBusy(false);
    }
  };

  const handleStockConfirm = async () => {
    if (!selectedId) return;
    setActionBusy(true);
    try {
      await apiService.updateMateriel(selectedId, {
        commercantId: null,
        status: 'EN_STOCK',
      });
      toast.success('Matériel remis en stock.');
      setStockOpen(false);
      await refreshAfterMutation(selectedId);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="fixed top-4 right-4 z-[60] space-y-2">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Matériels</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Parc Super Admin — inventaire par point de vente. Les indicateurs
            s&apos;appuient sur la configuration et le journal d&apos;appels cloud
            (pas de statut temps réel).
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          + Ajouter un matériel
        </button>
      </div>

      <MaterielKpiRow
        kpis={kpis}
        activeKpi={activeKpi}
        loading={loading}
        onSelect={(kpiId) => {
          setFilters((prev) => filtersFromKpi(kpiId, { ...prev, query: searchInput }));
        }}
      />

      <MaterielFiltersBar
        filters={filters}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onFiltersChange={setFilters}
        onChip={(chipId) => {
          setFilters((prev) =>
            filtersFromChip(chipId, { ...prev, query: searchInput })
          );
        }}
        onReset={clearFilters}
        onRefresh={load}
      />

      {hitApiLimit ? (
        <div
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        >
          Affichage limité aux {API_LIST_SOFT_LIMIT} premiers matériels renvoyés
          par l&apos;API. Les compteurs et filtres portent uniquement sur ce jeu
          chargé.
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          className={`lg:col-span-2 space-y-3 ${
            selectedItem ? 'order-2 lg:order-none' : ''
          }`}
        >
          {!loading && items.length > 0 ? (
            <p className="text-xs text-gray-500">
              {visible.length} résultat{visible.length > 1 ? 's' : ''}
              {visible.length !== items.length
                ? ` sur ${items.length} chargé${items.length > 1 ? 's' : ''}`
                : ''}
            </p>
          ) : null}

          {loading ? (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-14 rounded-md bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <MaterielEmptyState variant="parc" onPrimary={openAdd} />
          ) : visible.length === 0 ? (
            <MaterielEmptyState variant="filtre" onSecondary={clearFilters} />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-h-[70vh] overflow-y-auto">
              {visible.map((d) => (
                <MaterielListRow
                  key={d.id}
                  item={d}
                  selected={selectedId === d.id}
                  onSelect={(row) => openDetail(row.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className={selectedItem ? 'order-1 lg:order-none' : ''}>
          <MaterielDrawer
            key={selectedId || 'empty'}
            listItem={selectedItem}
            detail={detail}
            detailLoading={detailLoading}
            detailError={detailError}
            journal={journal}
            journalLoading={journalLoading}
            journalError={journalError}
            onClose={closeDetail}
            onRetryDetail={() => selectedId && loadDetail(selectedId)}
            onRetryJournal={() => selectedId && loadJournal(selectedId)}
            actionBusy={actionBusy}
            onAssign={() => {
              setAssignOpen(true);
              ensureCommerces();
            }}
            onStock={() => setStockOpen(true)}
          />
        </div>
      </div>

      <MaterielAddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAddSubmit}
        commerces={commerces}
        commercesLoading={commercesLoading}
        commercesError={commercesError}
        onRetryCommerces={loadCommerces}
      />
      <MaterielAssignModal
        open={assignOpen}
        item={drawerItem}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        commerces={commerces}
        commercesLoading={commercesLoading}
        commercesError={commercesError}
        onRetryCommerces={loadCommerces}
      />
      <MaterielStockConfirmModal
        open={stockOpen}
        item={drawerItem}
        onClose={() => setStockOpen(false)}
        onConfirm={handleStockConfirm}
      />
    </div>
  );
}
